import { Fragment, useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, BarChart3, ChevronDown, ChevronRight, GitBranch, Info, Layers, Minus, Plus, Table2, X } from "lucide-react";
import { branchMult, computeDelta, computeValue, deltaGoodness, stateFor, trendBuckets } from "@/lib/utils/daylight-insight/compute";
import { granularity, trendUnitLabel } from "@/lib/utils/daylight-insight/dates";
import { fmtVal, formatAbsDelta, unitLabel, unitName } from "@/lib/utils/daylight-insight/format";
import { COMPARE_PALETTE, SHOW_BENCHMARK_LABEL } from "@/lib/utils/daylight-insight/constants";
import { DIMENSIONS, breakdownRows, categoryRecords, dimensionMembers, dimsForMetric, isAdditive } from "@/lib/utils/daylight-insight/dimensions";
import type { DateRange, Metric, MetricKind, Registry, Service } from "@/lib/utils/daylight-insight/types";
import { useStore } from "@/provider/store";
import { MultiSelect, OptionSearch } from "./controls";
import { CompareChart, Legend, TrendChart, type CompareSeries } from "./charts";

function BreakdownBars({ metric, dimKey, range, onPick }: { metric: Metric; dimKey: string; range: DateRange; onPick: (mem: string) => void }) {
  const s = useStore();
  const rows = breakdownRows(metric, dimKey, range, dimensionMembers(dimKey, s.accessList, s.filterOptionsFor({ key: dimKey }).map((o) => o.label)));
  const max = Math.max(...rows.map((r) => r.value), metric.benchmark || 0) * 1.08 || 1;
  return (
    <div className="bd-bars">
      {rows.map((r) => {
        const st = stateFor(metric, r.value);
        const col = metric.benchmark == null ? "var(--chart-primary)" : st === "good" ? "var(--green)" : "var(--red)";
        return (
          <button key={r.mem} className="bd-bar-row" onClick={() => onPick(r.mem)} title={`See ${r.mem} records`}>
            <span className="bd-bar-lbl">{r.mem}</span>
            <span className="bd-bar-track">
              <span className="bd-bar-fill" style={{ width: (r.value / max) * 100 + "%", background: col }} />
              {metric.benchmark != null && <span className="bd-bench-mark" style={{ left: Math.min(100, (metric.benchmark / max) * 100) + "%" }} />}
            </span>
            <span className="bd-bar-val num">{fmtVal(metric, r.value)}</span>
          </button>
        );
      })}
      {SHOW_BENCHMARK_LABEL && metric.benchmark != null && <div className="bd-bench-note"><span className="bd-bench-tick" />Benchmark {metric.benchLabel}</div>}
    </div>
  );
}

function BreakdownTable({ metric, dimKey, range, service, open, setOpen }: { metric: Metric; dimKey: string; range: DateRange; service: Service; open: string | null; setOpen: (v: string | null) => void }) {
  const s = useStore();
  const dim = DIMENSIONS[dimKey];
  const rows = breakdownRows(metric, dimKey, range, dimensionMembers(dimKey, s.accessList, s.filterOptionsFor({ key: dimKey }).map((o) => o.label)));
  const total = computeValue(metric, range);
  const additive = isAdditive(metric);
  return (
    <table className="dl-tbl bd-tbl">
      <thead><tr><th>{dim.label}</th><th style={{ textAlign: "right" }}>Value</th><th style={{ textAlign: "right" }}>{additive ? "Share" : "vs Bench"}</th></tr></thead>
      <tbody>
        {rows.map((r) => {
          const st = stateFor(metric, r.value);
          const isOpen = open === r.mem;
          const recs = isOpen ? categoryRecords(metric, dimKey, r.mem, range, service, s.accessList) : [];
          return (
            <Fragment key={r.mem}>
              <tr className="bd-row" onClick={() => setOpen(isOpen ? null : r.mem)}>
                <td><span className="bd-exp">{isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>{r.mem}</td>
                <td className="num" style={{ textAlign: "right" }}>{fmtVal(metric, r.value)}</td>
                <td className={"num " + (additive ? "" : st)} style={{ textAlign: "right" }}>{additive ? (total ? (r.value / total * 100).toFixed(0) + "%" : "—") : (metric.benchmark != null ? (st === "good" ? "✓ beat" : "✗ miss") : "—")}</td>
              </tr>
              {isOpen && (
                <tr className="bd-subhead"><td colSpan={3}>{recs.length} {DIMENSIONS[dimKey].rec}{recs.length === 1 ? "" : "s"} · {r.mem}</td></tr>
              )}
              {isOpen && recs.map((rc, j) => (
                <tr key={j} className="bd-subrow">
                  <td><span className="bd-sub-name">{rc.name}</span>{rc.sub && <span className="bd-sub-tag">{rc.sub}</span>}</td>
                  <td className="num" style={{ textAlign: "right" }}>{fmtVal(metric, rc.value)}</td>
                  <td />
                </tr>
              ))}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function CompareToggle({ mode, setMode }: { mode: string; setMode: (m: string) => void }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="cmp-lbl" style={{ marginBottom: 6 }}>Compare across</div>
      <div className="dl-seg cmp-seg">
        <button className={mode === "branches" ? "on" : ""} onClick={() => setMode("branches")}>Branches</button>
        <button className={mode === "metrics" ? "on" : ""} onClick={() => setMode("metrics")}>Metrics</button>
      </div>
    </div>
  );
}

interface CompareViewProps {
  metric: Metric;
  range: DateRange;
  mode: string;
  setMode: (m: string) => void;
  branches: string[];
  setBranches: (s: string[]) => void;
  metrics: string[];
  setMetrics: (s: string[]) => void;
  reg: Registry;
  push: (target: string) => void;
}
function CompareView({ metric, range, mode, setMode, branches, setBranches, metrics, setMetrics, reg }: CompareViewProps) {
  const [pickOpen, setPickOpen] = useState(false);
  // Add-metric search — cleared whenever the picker closes so it reopens whole.
  // Declared above the branches-mode early return to keep the hook order fixed.
  const [pickQuery, setPickQuery] = useState("");
  useEffect(() => { if (!pickOpen) setPickQuery(""); }, [pickOpen]);
  // The user's live branch list — what compare-by-branch can choose from.
  const branchList = useStore().accessList;

  // ---------- BRANCHES MODE ----------
  if (mode === "branches") {
    const series: CompareSeries[] = branches.slice(0, 4).map((br, idx) => {
      const mult = branchMult(br, metric.id);
      const scaled: Metric = { ...metric, daily: metric.daily.map((v) => v * mult) };
      return { key: br, color: COMPARE_PALETTE[idx], kind: metric.kind, buckets: trendBuckets(scaled, range) };
    });
    return (
      <>
        <CompareToggle mode={mode} setMode={setMode} />
        <MultiSelect label="Branches" icon={<Layers size={14} style={{ color: "var(--ink-3)" }} />} options={branchList} selected={branches} onChange={(sel) => setBranches(sel.length ? sel.slice(0, 4) : branchList.slice(0, 1))} allLabel="All branches" />
        <CompareChart series={series} />
        <Legend items={series.map((s) => ({ label: s.key, color: s.color }))} />
      </>
    );
  }

  // ---------- METRICS MODE ----------
  const selected = [metric.id, ...metrics].slice(0, 4);
  const selKinds = [...new Set(selected.map((id) => reg[id].kind))];
  const axisFull = selKinds.length >= 2;        // already using both axes
  const series: CompareSeries[] = selected.map((id, idx) => {
    const m = reg[id];
    return { id, key: m.name, color: COMPARE_PALETTE[idx], kind: m.kind, buckets: trendBuckets(m, range) };
  });
  const rightKind = selKinds[1];
  const sideOf = (k: MetricKind) => (k === selKinds[0] ? "L" : "R");
  // a metric is addable only if total<4 AND its kind keeps us at ≤2 distinct scales
  const pickQ = pickQuery.trim().toLowerCase();
  const pool = Object.values(reg)
    .filter((m) => !selected.includes(m.id))
    .filter((m) => !pickQ || m.name.toLowerCase().includes(pickQ))
    .map((m) => ({ ...m, blocked: axisFull && !selKinds.includes(m.kind) }));
  return (
    <>
      <CompareToggle mode={mode} setMode={setMode} />
      <div className="cmp-subject">
        <span className="cmp-lbl">Subject</span>
        <span className="cmp-chip subject"><span className="cmp-dot" style={{ background: COMPARE_PALETTE[0] }} />{metric.name}{axisFull && <span className="cmp-axis">{sideOf(metric.kind)}</span>}</span>
      </div>
      <div className="cmp-compareto">
        <span className="cmp-lbl">Compare to</span>
        <div className="cmp-chips">
          {metrics.map((id, i) => (
            <span key={id} className="cmp-chip"><span className="cmp-dot" style={{ background: COMPARE_PALETTE[i + 1] }} />{reg[id].name}{axisFull && <span className="cmp-axis">{sideOf(reg[id].kind)}</span>}
              <button onClick={() => setMetrics(metrics.filter((x) => x !== id))} aria-label="remove"><X size={12} /></button>
            </span>
          ))}
          {selected.length < 4 && (
            <div style={{ position: "relative", display: "inline-block" }}>
              <button className="cmp-add" onClick={() => setPickOpen(!pickOpen)}><Plus size={13} />Add metric</button>
              {pickOpen && (
                <div className="dl-pop cmp-pop">
                  <OptionSearch query={pickQuery} setQuery={setPickQuery} placeholder="Search metrics…" />
                  {axisFull && <div className="cmp-poptip">A chart can show 2 scales ({unitLabel(selKinds[0])} and {unitLabel(rightKind)}). Other units are disabled.</div>}
                  {pool.map((m) => (
                    <div key={m.id} className={"dl-opt" + (m.blocked ? " disabled" : "")} onClick={() => { if (m.blocked) return; setMetrics([...metrics, m.id]); setPickOpen(false); }}>
                      <span style={{ flex: 1 }}>{m.name}</span>
                      <span style={{ fontSize: 10.5, color: m.blocked ? "var(--red)" : "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".03em" }}>{m.blocked ? "diff. scale" : m.kind}</span>
                    </div>
                  ))}
                  {pool.length === 0 && <div className="dl-noopt">{pickQ ? `No metrics match “${pickQuery.trim()}”.` : "No other metrics to compare."}</div>}
                </div>
              )}
            </div>
          )}
          <span className="cmp-count">{selected.length} of 4 max{axisFull ? " · 2 scales" : ""}</span>
        </div>
      </div>
      <CompareChart series={series} />
      <Legend items={series.map((s) => ({ label: s.key + (axisFull ? ` (${sideOf(s.kind)})` : ""), color: s.color }))} />
      {axisFull && <div className="cmp-note"><Info size={13} />Two scales in play — left axis is in {unitName(selKinds[0])}, right axis in {unitName(rightKind)}. A chart shows at most two.</div>}
    </>
  );
}

export function DrillPanel() {
  const { reg, range, drillStack, setDrillStack, closeDrill, service } = useStore();
  const [tab, setTab] = useState("chart");
  const [view, setView] = useState("trend");        // "trend" | dimension key
  const [bdOpen, setBdOpen] = useState<string | null>(null);        // expanded category in breakdown table
  const [compareBranches, setCompareBranches] = useState(["Main", "Northside"]);
  const [compareMode, setCompareMode] = useState("branches");
  const [compareMetrics, setCompareMetrics] = useState<string[]>([]);
  const id = drillStack[drillStack.length - 1];
  const metric = reg[id];
  useEffect(() => { setView("trend"); setBdOpen(null); }, [id]);
  useEffect(() => { setBdOpen(null); }, [view]);
  if (!metric) return null;
  const dims = dimsForMetric(metric);
  const pickMember = (mem: string) => { setTab("table"); setBdOpen(mem); };
  const value = computeValue(metric, range);
  const delta = computeDelta(metric, range);
  const st = stateFor(metric, value);
  const dg = deltaGoodness(metric, delta.pct);
  const push = (target: string) => target && reg[target] && setDrillStack([...drillStack, target]);
  return (
    <>
      <div className="dl-scrim" onClick={closeDrill} />
      <div className="dl-drill dl">
        <div className="dl-drillhead">
          <div className="dl-bread">
            {drillStack.map((sid, i) => (
              <Fragment key={sid + i}>
                {i > 0 && <ChevronRight size={11} />}
                {i < drillStack.length - 1
                  ? <button onClick={() => setDrillStack(drillStack.slice(0, i + 1))}>{reg[sid].name}</button>
                  : <span style={{ color: "var(--ink-1)", fontWeight: 600 }}>{reg[sid].name}</span>}
              </Fragment>
            ))}
            <div style={{ flex: 1 }} />
            <button onClick={closeDrill} style={{ color: "var(--ink-3)" }}><X size={16} /></button>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
            <div className="num" style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-.02em" }}>{fmtVal(metric, value)}</div>
            <span className={"dl-delta num " + dg} style={{ fontSize: 14 }}>
              {dg === "good" ? <ArrowUpRight size={15} /> : dg === "bad" ? <ArrowDownRight size={15} /> : <Minus size={13} />}{formatAbsDelta(metric, delta)}
            </span>
            {SHOW_BENCHMARK_LABEL && metric.benchmark != null && <span className={"dl-bench bench-" + st}>Benchmark {metric.benchLabel}</span>}
          </div>
          <div className="dl-formula"><strong style={{ color: "var(--ink-1)" }}>Formula · </strong>{metric.formula}</div>
          <div className="dl-drilltabs">
            <button className={tab === "chart" ? "on" : ""} onClick={() => setTab("chart")}><BarChart3 size={13} />Chart</button>
            <button className={tab === "table" ? "on" : ""} onClick={() => setTab("table")}><Table2 size={13} />Table</button>
            <button className={tab === "compare" ? "on" : ""} onClick={() => setTab("compare")}><GitBranch size={13} />Compare</button>
          </div>
        </div>
        <div className="dl-drillbody">
          {tab !== "compare" && dims.length > 0 && (
            <div className="bd-switch">
              <span className="bd-switch-lbl">View</span>
              <button className={view === "trend" ? "on" : ""} onClick={() => setView("trend")}>Trend</button>
              {dims.map((d) => <button key={d} className={view === d ? "on" : ""} onClick={() => setView(d)}>By {DIMENSIONS[d].label}</button>)}
            </div>
          )}
          {tab === "chart" && (
            view === "trend" ? (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>{trendUnitLabel(granularity(range))}</div>
                <TrendChart metric={metric} range={range} />
              </>
            ) : (
              <>
                <div className="bd-cap">{metric.name} by {DIMENSIONS[view].label} · click a bar to see {DIMENSIONS[view].rec}s</div>
                <BreakdownBars metric={metric} dimKey={view} range={range} onPick={pickMember} />
              </>
            )
          )}
          {tab === "table" && (
            view === "trend" ? (
              <table className="dl-tbl">
                <thead><tr><th>Period</th><th style={{ textAlign: "right" }}>Value</th><th style={{ textAlign: "right" }}>vs Bench</th></tr></thead>
                <tbody>
                  {trendBuckets(metric, range).map((b, i) => {
                    const bs = stateFor(metric, b.value);
                    return <tr key={i}><td>{b.label}</td><td className="num" style={{ textAlign: "right" }}>{fmtVal(metric, b.value)}</td><td className={"num " + bs} style={{ textAlign: "right" }}>{metric.benchmark != null ? (bs === "good" ? "✓" : "✗") : "—"}</td></tr>;
                  })}
                </tbody>
              </table>
            ) : (
              <>
                <div className="bd-cap">{metric.name} by {DIMENSIONS[view].label} · click a row to expand to {DIMENSIONS[view].rec}s</div>
                <BreakdownTable metric={metric} dimKey={view} range={range} service={service} open={bdOpen} setOpen={setBdOpen} />
              </>
            )
          )}
          {tab === "compare" && (
            <>
              <div className="cmp-banner">
                <span><Info size={15} style={{ color: "var(--hermes)", flexShrink: 0 }} />Compare mode overrides your branch filter — showing only the {compareMode === "metrics" ? "metrics" : "branches"} selected below.</span>
                <button onClick={() => setTab("chart")}>Exit compare</button>
              </div>
              <CompareView metric={metric} range={range}
                mode={compareMode} setMode={setCompareMode}
                branches={compareBranches} setBranches={setCompareBranches}
                metrics={compareMetrics} setMetrics={setCompareMetrics}
                reg={reg} push={push} />
            </>
          )}
          {metric.drivers && tab !== "compare" && (
            <div className="dl-drivers">
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 11 }}>Driver tree</div>
              {metric.drivers.map((dr, i) => {
                const clickable = !!dr.target && reg[dr.target];
                return (
                  <div key={i} className={"dl-driver" + (dr.level === "L3" ? " l3" : "") + (clickable ? " click" : "")} onClick={() => clickable && dr.target && push(dr.target)}>
                    <div className="dl-drow">
                      <span className="dl-dlvl">{dr.level}</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{dr.name}</div>
                        <div className="dl-drel">{dr.rel}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {dr.val && <span className="num" style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)" }}>{dr.val}</span>}
                      {clickable && <ChevronRight size={15} style={{ color: "var(--hermes)" }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
